import { DisplayRailwayCompany } from './createStationDisplayData';
import { DisplayArea } from '../../LocationModal/logics/createLocationDisplayData';
import {
  updateSelectedAssociatedWithStation,
  updateSelectedArea,
} from './selectStationLogic';

export type CompositeIdString = `${string}:${string}`;

/**
 * チェック状態を管理するインターフェース
 */
export interface CheckState {
  /** 選択中やチェックリストの選択変数 */
  selectedArea: string[];
  selectedPref: string[];
  selectedRailwayCompany: CompositeIdString[];
  selectedRoute: CompositeIdString[];
  checkedAllRoute: string[];
  checkedStation: string[];
  areas: DisplayArea[];
}

/**
 * 初期のチェック状態
 */
export const initialState: CheckState = {
  selectedArea: [],
  selectedPref: [],
  selectedRailwayCompany: [],
  selectedRoute: [],
  checkedAllRoute: [],
  checkedStation: [],
  areas: [],
};

/**
 * リデューサーアクションの種類
 */
export type ReducerAction =
  | {
      type: 'init';
      master: DisplayRailwayCompany[];
    }
  | {
      type: 'checkStation';
      value: {
        stationId: string;
        routeId: string;
      };
      master: DisplayRailwayCompany[];
    }
  | {
      type: 'checkRoute';
      value: {
        railwayCompanyId: string;
        routeId: string;
      };
      master: DisplayRailwayCompany[];
    }
  | {
      type: 'update';
      payload: Partial<CheckState>;
    }
  | {
      type: 'reset';
    };

/**
 * チェック状態を更新するためのリデューサー
 * @param {CheckState} state - 現在のチェック状態
 * @param {ReducerAction} action - 実行するアクション
 * @returns {CheckState} 更新後のチェック状態
 */
export const reducer = (
  state: CheckState,
  action: ReducerAction
): CheckState => {
  switch (action.type) {
    case 'init': {
      // 都道府県、市区郡、区の選択済みと路線チェック済みを取得する
      const checkedAllRoute = [...state.checkedAllRoute];
      const updateSelectedStationDetail = updateSelectedAssociatedWithStation(
        state.checkedStation,
        checkedAllRoute,
        action.master
      );
      const returnUpdateSelectedArea = updateSelectedArea(
        updateSelectedStationDetail.selectedPref,
        state.areas
      );
      return {
        ...state,
        selectedArea: returnUpdateSelectedArea,
        selectedPref: updateSelectedStationDetail.selectedPref,
        selectedRailwayCompany:
          updateSelectedStationDetail.selectedRailwayCompany,
        selectedRoute: updateSelectedStationDetail.selectedRoute,
      };
    }
    case 'checkStation': {
      const { stationId, routeId } = action.value;
      // 駅のindexがcheckedStationに存在するか確認する。
      const idx = state.checkedStation.indexOf(stationId);
      // 更新用の選択済み駅配列を用意する
      const updateCheckedStation = [...state.checkedStation];
      const checkedAllRoute = [...state.checkedAllRoute];
      // 駅を更新する。
      if (idx === -1) {
        updateCheckedStation.push(stationId);
      } else {
        updateCheckedStation.splice(idx, 1);
      }

      // 都道府県、市区郡、区の選択済みと路線チェック済みを取得する
      const updateSelectedStationDetail = updateSelectedAssociatedWithStation(
        updateCheckedStation,
        checkedAllRoute,
        action.master,
        routeId
      );

      return {
        ...state,
        ...updateSelectedStationDetail,
        // 地域の選択済みを取得する
        selectedArea: updateSelectedArea(
          updateSelectedStationDetail.selectedPref,
          state.areas
        ),
        checkedStation: updateCheckedStation,
      };
    }
    case 'checkRoute': {
      // 更新用の選択済み駅配列を用意する
      const updateCheckedStation = [...state.checkedStation];
      const updateCheckedAllRoute = [...state.checkedAllRoute];
      const { railwayCompanyId, routeId } = action.value;
      // 沿線以下の駅を全てチェックする。
      // 企業データから更新があった沿線を確認する。
      for (let i = 0; i < action.master.length; i += 1) {
        const comp = action.master[i];
        if (railwayCompanyId === comp.id) {
          // 沿線から駅を確認する。
          for (let j = 0; j < comp.routeList.length; j += 1) {
            const route = comp.routeList[j];
            const routeIdx = state.checkedAllRoute.indexOf(route.id);
            if (routeId === route.id) {
              // 駅全てを更新する。
              for (let k = 0; k < route.stationList.length; k += 1) {
                const station = route.stationList[k];
                const stationIdx = updateCheckedStation.indexOf(station.id);
                // routeが選択されていれば対象の駅を全選択、未選択なら選択解除
                if (routeIdx === -1 && stationIdx === -1) {
                  updateCheckedStation.push(station.id);
                } else if (routeIdx > -1) {
                  updateCheckedStation.splice(stationIdx, 1);
                }
              }
              break;
            }
          }
          break;
        }
      }

      // 都道府県、市区郡、区の選択済みと路線チェック済みを取得する
      const updateSelectedStationDetail = updateSelectedAssociatedWithStation(
        updateCheckedStation,
        updateCheckedAllRoute,
        action.master,
        routeId
      );

      return {
        ...state,
        ...updateSelectedStationDetail,
        // 地域の選択済みを取得する
        selectedArea: updateSelectedArea(
          updateSelectedStationDetail.selectedPref,
          state.areas
        ),
        checkedStation: updateCheckedStation,
      };
    }
    case 'update':
      return {
        ...state,
        ...action.payload,
      };
    case 'reset': {
      return {
        selectedArea: [],
        selectedPref: [],
        selectedRailwayCompany: [],
        selectedRoute: [],
        checkedAllRoute: [],
        checkedStation: [],
        areas: state.areas,
      };
    }
    default:
      throw new Error();
  }
};
